import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Share2 } from "lucide-react";
import { StatusPill } from "@/components/brand";
import { ProofOfDelivery } from "@/components/ProofOfDelivery";
import MapView from "@/components/MapView";
import api from "@/lib/api";

export default function OrderDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/deliveries/${id}`);
    setD(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const inTransit = ["assigned", "picked_up", "en_route"].includes(d?.status);
  useEffect(() => {
    if (!inTransit) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [inTransit, load]);

  if (!d) return <div className="text-sm text-gray-400 font-mono uppercase tracking-wide">Loading…</div>;

  const trackingUrl = d.tracking_token ? `${window.location.origin}/track/${d.tracking_token}` : null;
  const shareTracking = async () => {
    if (!trackingUrl) return;
    try { await navigator.clipboard.writeText(trackingUrl); toast.success("Tracking link copied — share it with your customer"); }
    catch { toast.error("Could not copy link"); }
  };

  const loc = inTransit ? d.driver_location : null;
  const points = [
    { lat: d.pickup.lat, lng: d.pickup.lng, label: `Pickup · ${d.pickup.address}`, type: "pickup" },
    { lat: d.dropoff.lat, lng: d.dropoff.lng, label: `Dropoff · ${d.dropoff.address}`, type: "dropoff" },
    ...(loc ? [{ lat: loc.lat, lng: loc.lng, label: `${d.driver_name || "Driver"} · ${new Date(loc.at).toLocaleTimeString()}`, type: "driver", eta: d.eta_min }] : []),
  ];
  const route = points.filter((p) => p.type !== "driver");
  const trails = loc && d.driver_trail ? [d.driver_trail] : [];

  return (
    <div data-testid="order-detail">
      <Link to="/merchant/orders" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0a0a0a] mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to orders
      </Link>

      <div className="flex flex-wrap items-center gap-4 mb-2">
        <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">ORDER · {d.id.slice(0, 8).toUpperCase()}</span>
        <StatusPill status={d.status} />
        {trackingUrl && (
          <button onClick={shareTracking} data-testid="copy-tracking-link" className="ml-auto inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.1em] uppercase border border-gray-300 text-[#0a0a0a] px-3 py-1.5 hover:border-orange-500 hover:text-orange-600 transition-colors">
            <Share2 className="h-3.5 w-3.5" /> Copy tracking link
          </button>
        )}
      </div>
      <h1 className="text-2xl font-bold tracking-tight text-[#0a0a0a] mb-8">{d.package_description}</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <MapView points={points} route={route} trails={trails} height={340} follow={inTransit} />
          {inTransit && (
            <div className="flex flex-wrap items-center gap-4 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400" data-testid="live-tracking-status">
              <span className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${loc ? "bg-orange-500 animate-pulse" : "bg-gray-300"}`} />
                {loc ? `Live · driver seen ${new Date(loc.at).toLocaleTimeString()}` : "Live tracking · waiting for driver location"}
              </span>
              {d.eta_min != null && (
                <span className="text-orange-600" data-testid="live-eta">Arriving in ~{d.eta_min} min · {d.distance_km} km away</span>
              )}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
            <div className="bg-white p-5">
              <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-2"><MapPin className="h-3.5 w-3.5" /> Pickup</div>
              <div className="text-sm text-[#0a0a0a]">{d.pickup.address}</div>
            </div>
            <div className="bg-white p-5">
              <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-orange-600 mb-2"><MapPin className="h-3.5 w-3.5" /> Dropoff</div>
              <div className="text-sm text-[#0a0a0a]">{d.dropoff.address}</div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="border border-gray-200 p-5">
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-3">Assignment</div>
            <div className="text-sm text-[#0a0a0a]">{d.driver_name || "Awaiting driver"}</div>
            <div className="mt-3 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400">ETA</div>
            <div className="text-sm text-[#0a0a0a]">{new Date(d.window_end).toLocaleString()}</div>
            <div className="mt-3 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400">Cost</div>
            <div className="text-sm text-[#0a0a0a]">${d.cost.toFixed(2)}</div>
          </div>

          <ProofOfDelivery delivery={d} />

          <div className="border border-gray-200 p-5">
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-4">Status timeline</div>
            <ol className="space-y-4" data-testid="order-timeline">
              {d.timeline.map((t, i) => (
                <li key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span className={`h-2.5 w-2.5 rounded-full ${i === d.timeline.length - 1 ? "bg-orange-500" : "bg-[#0a0a0a]"}`} />
                    {i < d.timeline.length - 1 && <span className="w-px flex-1 bg-gray-200 my-1" />}
                  </div>
                  <div>
                    <div className="text-sm text-[#0a0a0a] capitalize">{t.status.replace("_", " ")}</div>
                    <div className="font-mono text-[10px] text-gray-400">{new Date(t.at).toLocaleString()}</div>
                    {t.note && <div className="text-xs text-gray-500 mt-0.5">{t.note}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
